import type { DocPage } from "../types";

export const page: DocPage = {
  slug: "backtesting",
  title: "Backtests and results",
  navLabel: "Backtesting",
  description:
    "Run a historical test for one Strategy version, understand its results and assumptions, and decide what evidence to gather next.",
  section: "strategies",
  published: "2026-08-03",
  updated: "2026-08-03",
  targets: ["Mavori backtesting", "run a strategy backtest", "read backtest results"],
  related: ["strategies", "deployments", "data"],
  blocks: [
    {
      type: "lead",
      text: "A backtest replays one saved Strategy version over historical data and reports the trades and account path produced by its rules. Use it to inspect behavior under stated assumptions, compare versions, and find what needs more testing.",
    },

    { type: "h2", text: "Prepare the version" },
    {
      type: "p",
      text: "Open the Strategy and confirm its instruments, bar type, rules, and source on **Setup**. On **Backtest**, choose the date window and trading-cost assumptions shown for the run. These settings belong to the evidence you are about to read, so choose values that match the market and holding period you intend to study.",
    },
    {
      type: "p",
      text: "Run the pre-check first. It verifies that the version can be evaluated and that the requested data is usable. If Mavori identifies missing or partial coverage, resolve the named issue or narrow the test before treating the result as representative.",
    },

    { type: "h2", text: "Run and read the report" },
    {
      type: "steps",
      items: [
        {
          title: "Select the version",
          text: "Use the version list on the Backtest tab. The report always belongs to the selected version.",
        },
        {
          title: "Set the test",
          text: "Review the date window, instruments, cadence, and cost assumptions.",
        },
        {
          title: "Run the backtest",
          text: "Start the run and return when the result is ready; the Strategy keeps the completed report.",
        },
        {
          title: "Read behavior before return",
          text: "Start with coverage, trade count, max drawdown, and the equity curve, then read total return, win rate, benchmark comparison, and individual trades.",
        },
      ],
    },

    { type: "h2", text: "Interpret the result" },
    {
      type: "table",
      caption: "The first evidence to review",
      headers: ["Result", "What to look for"],
      rows: [
        ["Coverage", "Whether the requested instruments and period had enough usable data."],
        ["Trade count", "Whether the result rests on a meaningful number of decisions."],
        ["Max drawdown", "The largest decline the tested account experienced."],
        ["Equity curve", "Whether gains were broad or depended on a short favorable stretch."],
        ["Costs and assumptions", "Whether fills and friction are plausible for the strategy."],
        ["Trade list", "Which entries, exits, symbols, and periods drove the summary."],
      ],
    },
    {
      type: "callout",
      variant: "warning",
      title: "A backtest is an estimate",
      text: "Historical performance does not establish that a Strategy will work. Before deployment, test a different period, keep costs realistic, investigate concentrated gains or losses, and confirm that later edits are saved as a new version.",
    },

    { type: "h2", text: "Choose the next step" },
    {
      type: "p",
      text: "If the run exposes a rule problem, return to [Strategies and versions](/docs/strategies), fork the version, and change one idea at a time. If the behavior remains understandable across meaningful tests, review the target account, capital, and Risk Controls before creating a [Strategy Deployment](/docs/deployments).",
    },
  ],
};
